import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, Card, Grid, Icon } from 'semantic-ui-react';
import { NHN_PWN_disabled, pageTitle, roles, userAllowed } from '../../helpers/utils';
import { onLoad, onUnload } from '../../actions/pages';
import { kpisThresoldModify, kpisView, kpiThresoldDefault } from '../../actions/performances';
import ErrorMessage from '../ErrorMessage';
import LoadingPage from '../LoadingPage';
import downlinkThroughPutImg from '../../semantic-ui/site/assets/download-blk.png';
import uploadThroughPutImg from '../../semantic-ui/site/assets/upload-blk.png';
import combined_throughput from '../../semantic-ui/site/assets/combined_throughput.svg';
import routerBlk from '../../semantic-ui/site/assets/router-blk.png';
import remindMeBlk from '../../semantic-ui/site/assets/remind-me-blk.png';
import attachFailure from '../../semantic-ui/site/assets/attach_failure.png';
import { OverallWidget } from './overallWidget';
import { decorateOverallKPI, getAttachedDevicesValue } from '../../helpers/performanceUtil';
import { SOLUTION_TYPE } from '../../helpers/const';
import './Performance.less';

const KPI_INFO_MAP = {
    ATTACHED_DEVICES: { name: 'Attached devices', image: routerBlk, maxVal: 5000 },
    COMBINED_THROUGHPUT: { name: 'Combined throughput', image: combined_throughput },
    DOWNLINK_THROUGHPUT: { name: 'Core downlink throughput', image: downlinkThroughPutImg, maxVal: 8 * 1000 },
    UPLINK_THROUGHPUT: { name: 'Core uplink throughput', image: uploadThroughPutImg, maxVal: 4 * 1000 },
    ATTACH_FAILURE_RATIO: { name: 'Attach failure', image: attachFailure },
    SERVICE_REQUEST_FAILURE_RATIO: { name: 'Service request failure', image: remindMeBlk }
};

export const getInfo = (OverallKPIType) => KPI_INFO_MAP[OverallKPIType] || {};

export const widgetSections = {
    CORE_EP5G: '4G/5G core',
    CORE_4G: '4G core',
    CORE_5G: '5G core',
    RAN_DATA: '4G RAN data',
    RAN_VOICE: '4G RAN voice',
    NSA_RAN_DATA: '5G NSA RAN data',
    RAN_DATA_5G: '5G RAN data'
};

const KPIViewPage = (props) => {
    const { reload, setReload } = props;
    const { enterpriseId, locationId, roles: userRoles, isIBC, solutionType, isFLEX, network } = useSelector((state) => state.auth);

    const isEP5G = useMemo(() => solutionType === SOLUTION_TYPE.EP5G, [solutionType]);
    const isNhnPwn = useMemo(() => solutionType === SOLUTION_TYPE.NHN_PWN, [solutionType]);
    const isNDAC = useMemo(() => solutionType === SOLUTION_TYPE.NDAC, [solutionType]);
    const is5GSA = useMemo(() => network === 'SA_5G', [network]);
    const isLTEOrNSA = useMemo(() => network === 'LTE' || network === 'NSA_5G', [network]);
    const isEP5GOrNhnPwn = useMemo(() => solutionType === SOLUTION_TYPE.EP5G || solutionType === SOLUTION_TYPE.NHN_PWN, [solutionType]);
    const isKPI4GAttachedDevices = isEP5G
        ? userAllowed(userRoles, [roles.MANAGE_4G_ATTACHED_DEVICES, roles.MANAGE_5GSA_REGISTERED_DEVICES], true)
        : userAllowed(userRoles, [roles.MANAGE_4G_ATTACHED_DEVICES]);
    const isKPI4GAttachSuccessRate = userAllowed(userRoles, [roles.MANAGE_4G_ATTACH_SUCCESS_RATE]);
    const isKPI4GCoreDLThroughput = userAllowed(userRoles, [roles.MANAGE_4G_CORE_DL_THROUGHPUT]);
    const isKPI4GCoreULThroughput = userAllowed(userRoles, [roles.MANAGE_4G_CORE_UL_THROUGHPUT]);

    const isKPI5GSARegisteredDevices = userAllowed(userRoles, [roles.MANAGE_5GSA_REGISTERED_DEVICES]);
    const isKPI5GSARegistrationSuccessRate = userAllowed(userRoles, [roles.MANAGE_5GSA_REGISTRATION_SUCCESS_RATE]);
    const isKPI5GCoreDLThroughput = userAllowed(userRoles, [roles.MANAGE_5G_CORE_DL_THROUGHPUT]);
    const isKPI5GCoreULThroughput = userAllowed(userRoles, [roles.MANAGE_5G_CORE_UL_THROUGHPUT]);

    const isKPI4GCoreAttachedDevices = userAllowed(userRoles, [roles.MANAGE_4G_ATTACHED_DEVICES]);
    const isKPI4GCoreAttachSuccessRate = userAllowed(userRoles, [roles.MANAGE_4G_ATTACH_SUCCESS_RATE]);
    const isKPI4GCoreNetworkUtilization = [SOLUTION_TYPE.EPG, SOLUTION_TYPE.EP5G, SOLUTION_TYPE.NHN_PWN].includes(solutionType);
    const isKPI4GCoreDLThroughputNew = userAllowed(userRoles, [roles.MANAGE_4G_CORE_DL_THROUGHPUT]);
    const isKPI4GCoreULThroughputNew = userAllowed(userRoles, [roles.MANAGE_4G_CORE_UL_THROUGHPUT]);

    const show5GCoreSection =
        isNDAC && is5GSA && (isKPI5GSARegisteredDevices || isKPI5GSARegistrationSuccessRate || isKPI5GCoreDLThroughput || isKPI5GCoreULThroughput);
    const show4GCoreSection =
        isNDAC &&
        isLTEOrNSA &&
        (isKPI4GCoreAttachedDevices || isKPI4GCoreAttachSuccessRate || isKPI4GCoreDLThroughputNew || isKPI4GCoreULThroughputNew);

    const isKPI4GRANDLThroughput = userAllowed(userRoles, [roles.MANAGE_4G_RAN_DL_THROUGHPUT]);
    const isKPI4GRANULThroughput = userAllowed(userRoles, [roles.MANAGE_4G_RAN_UL_THROUGHPUT]);
    const isKPI4GRANDataAccessibility = (isIBC || isEP5GOrNhnPwn ? true : isFLEX) && userAllowed(userRoles, [roles.KPI_4GRAN_DATA_ACCESSIBILITY]);
    const isKPI4GRANDataRetainability = (isIBC || isEP5GOrNhnPwn ? true : isFLEX) && userAllowed(userRoles, [roles.KPI_4GRAN_DATA_RETAINABILITY]);
    const isKPI4GRANDataMobility = (isIBC || isEP5GOrNhnPwn ? true : isFLEX) && userAllowed(userRoles, [roles.KPI_4GRAN_DATA_MOBILITY]);
    const isKPIVoLTEAccessibility = userAllowed(userRoles, [roles.VIEW_KPI_VoLTE_ACCESSIBILITY]);
    const isKPIVoLTERetainability = userAllowed(userRoles, [roles.VIEW_KPI_VoLTE_RETAINABILITY]);
    const isKPIVoLTEMobility = userAllowed(userRoles, [roles.VIEW_KPI_VoLTE_MOBILITY]);
    const isKPI5GRANDLThroughput = userAllowed(userRoles, [roles.MANAGE_5G_RAN_DL_THROUGHPUT]);
    const isKPI5GRANULThroughput = userAllowed(userRoles, [roles.MANAGE_5G_RAN_UL_THROUGHPUT]);
    const isKPI5GNSAAccessibility = userAllowed(userRoles, [roles.VIEW_KPI_5G_NSA_ACCESSIBILITY]);
    const isKPI5GNSARetainability = userAllowed(userRoles, [roles.VIEW_KPI_5G_NSA_RETAINABILITY]);
    const isKPI5GNSAMobility = userAllowed(userRoles, [roles.VIEW_KPI_5G_NSA_MOBILITY]);

    const reduxDispatch = useDispatch();
    const eventNotification = useSelector((state) => state.websocket.event);
    const [loading, setLoading] = useState(true);

    const [errorMessage, setErrorMessage] = useState();

    const [kpiDataView, setKpiData] = useState([]);
    const [kpiDefaultData, setKpiDefaultData] = useState([]);
    const [refreshInterval, setRefreshInterval] = useState();
    const [totalDeviceCount, setTotalDeviceCount] = useState();
    const [coreCapacity, setCoreCapacity] = useState();
    const [attachedDevicesCount, setAttachedDevicesCount] = useState(0);
    const [activeIndices, setActiveIndices] = useState(Object.keys(widgetSections).map((_, index) => index));

    const kpisViewInfo = (payload) => reduxDispatch(kpisView(payload));
    const kpiThresholdUpdate = (json) => reduxDispatch(kpisThresoldModify(json));
    const kpiThresholdDefaultValue = (json) => reduxDispatch(kpiThresoldDefault(json));
    // const generateCsv = (json) => reduxDispatch(generateReportCsv(json));
    const isEricssonSolution = useMemo(() => [SOLUTION_TYPE.EPG, SOLUTION_TYPE.EP5G].includes(solutionType), [solutionType]);
    const isNDACSolution = useMemo(() => solutionType === SOLUTION_TYPE.NDAC, [solutionType]);
    const is4gNDAC = isNDACSolution && (network === 'NSA_5G' || network === 'LTE');
    const is5GNetwork = network === 'NSA_5G' || network === 'SA_5G';
    const isEP5G_NHN_PWN_Or_NDAC_5G = (isEP5G || isEP5GOrNhnPwn || isNDACSolution) && is5GNetwork;

    const fetchData = async (type) => {
        try {
            const payload = {};
            payload.enterpriseId = enterpriseId;
            payload.locationId = locationId;
            setLoading(true);

            if (type === 'RELOAD_REFRESH') {
                await reduxDispatch(kpisViewInfo(payload));
            } else {
                const {
                    responseStatus,
                    responseData,
                    defaultData,
                    kpiMetricLastUpdateDate,
                    totalDeviceCount,
                    coreCapacity = 0
                } = await kpisViewInfo(payload);
                if (responseStatus === 200) {
                    setKpiData(responseData);
                    setKpiDefaultData(defaultData);
                    reduxDispatch({ type: 'KPI_OVERVIEW_DATA', payload: { kpiOverviewData: responseData, kpiMetricLastUpdateDate } });
                    setLoading(false);
                    setTotalDeviceCount(totalDeviceCount);
                    setCoreCapacity(coreCapacity);
                    setAttachedDevicesCount(getAttachedDevicesValue(responseData));
                }
            }
        } catch ({ message }) {
            setErrorMessage(message);
        } finally {
            setLoading(false);
        }
    };

    const submitModifyKpi = async (payload) => {
        try {
            const { responseStatus, responseData } = await kpiThresholdUpdate(payload);
            if (responseStatus === 200) {
                const val = responseData.find((thresold) => thresold.kpiType === payload.kpiType);
                setKpiData(responseData);
                reduxDispatch({ type: 'KPI_OVERVIEW_DATA', payload: { kpiOverviewData: responseData } });
                setLoading(false);
            }
        } catch ({ errorMessage: errors }) {
            setErrorMessage(errors);
        } finally {
            setLoading(false);
        }
    };

    /**
     *
     */
    useEffect(() => {
        fetchData();
        // refresh page every 15 mins
        setRefreshInterval(setInterval(fetchData, 15 * 60 * 1000));
        return () => {
            clearInterval(refreshInterval);
            setRefreshInterval();
        };
    }, []);

    /**
     * Handles:
     *   componentDidMount
     *   componentWillUnmount
     *
     */
    useEffect(() => {
        // componentDidMount
        reduxDispatch(onLoad('KPI_VIEW'));
        document.title = pageTitle('KPIs');

        // componentWillUnmount
        return () => {
            reduxDispatch(onUnload('KPI_VIEW'));
        };
    }, [submitModifyKpi]);

    /**
     * When we initially load the page there might be no data in cache.  We are forced to show loader until
     * the server side sends a DATA_SYNC notification, then we load the data.
     */
    useEffect(() => {
        if (eventNotification) {
            const { notificationType, message } = eventNotification;

            if (notificationType === 'DATA_SYNC' && message === 'SUCCESS') {
                fetchData('LOAD');
            }
        }
    }, [eventNotification]);

    useEffect(() => {
        if (reload) {
            fetchData();
            setReload(false);
        }
    }, [reload]);

    const errorMessageDismiss = () => setErrorMessage();

    //attach device
    const attachedDevicesKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ATTACHED_DEVICES', false);
    const enabledCount = attachedDevicesKPI?.value ? Number(attachedDevicesKPI?.value) : 0;
    const deviceCount = {
        enabledCount,
        offlineCount: totalDeviceCount - enabledCount,
        totalCount: totalDeviceCount
    };

    //attach success ratio
    const attachedSuccessRatioKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ATTACH_SUCCESS', false);
    const successCount = attachedSuccessRatioKPI?.value ? Number(attachedSuccessRatioKPI?.value) : 0;
    const successDeviceCount = {
        successCount,
        failuresCount: totalDeviceCount - successCount,
        totalCount: `${Math.round(((successCount * 100) / totalDeviceCount) * 10) / 10}%`
    };

    // accessibility
    const accessibilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ENB_ACCESSIBILITY', true);

    // mobility
    const mobilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ENB_MOBILITY', true);

    // retainability
    const retainabilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ENB_RETAINABILITY', true);

    // GNB accessibility
    const outdoor5gnbAccessibilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'OUTDOOR5GNB_ACCESSIBILITY', true);

    // GNB retainability
    const outdoor5gnbRetainabilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'OUTDOOR5GNB_RETAINABILITY', true);

    // GNB mobility
    const outdoor5gnbMobilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'OUTDOOR5GNB_MOBILITY', true);

    //attach rate
    const attachRateKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ATTACH_SUCCESS', true);

    // up link
    const upLinkKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, `${isIBC ? 'UL_CELL_THROUGHPUT' : 'UPLINK_THROUGHPUT'}`, false);

    // down link
    const downLinkKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, `${isIBC ? 'DL_CELL_THROUGHPUT' : 'DOWNLINK_THROUGHPUT'}`, false);

    // GNB donwlink throughput
    const gnbDownLinkKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'GNB_DOWNLINK_THROUGHPUT', false);

    // GNB uplink throughput
    const gnbUpLinkKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'GNB_UPLINK_THROUGHPUT', false);

    //5g RAN combined Throughput KPI for IBC
    const throughputValues5gRAN =
        gnbUpLinkKPI &&
        gnbDownLinkKPI &&
        [gnbDownLinkKPI, gnbUpLinkKPI].map((item) =>
            item?.value >= 1000 ? Math.round((item?.value / 1000) * 10) / 10 + 'k' : Math.round(item?.value * 10) / 10
        );

    //combinedThroughput KPI:
    const throughputValues =
        upLinkKPI &&
        downLinkKPI &&
        [downLinkKPI, upLinkKPI].map((item) =>
            item?.value >= 1000 ? Math.round((item?.value / 1000) * 10) / 10 + 'k' : Math.round(item?.value * 10) / 10
        );

    //4gThroughput KPI on 4gNDAC:
    // up link
    const upLink4gKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ENB_UPLINK_THROUGHPUT', false);
    // down link
    const downLink4gKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ENB_DOWNLINK_THROUGHPUT', false);

    const throughputValues4g =
        upLink4gKPI &&
        downLink4gKPI &&
        [downLink4gKPI, upLink4gKPI].map((item) =>
            item?.value >= 1000 ? Math.round((item?.value / 1000) * 10) / 10 + 'k' : Math.round(item?.value * 10) / 10
        );

    //5g NSA Throughput KPI on NDAC:
    // up link
    const upLink5gKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'OUTDOOR5GNB_UPLINK_THROUGHPUT', false);
    // down link
    const downLink5gKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'OUTDOOR5GNB_DOWNLINK_THROUGHPUT', false);

    const throughputValues5g =
        upLink5gKPI &&
        downLink5gKPI &&
        [downLink5gKPI, upLink5gKPI].map((item) =>
            item?.value >= 1000 ? Math.round((item?.value / 1000) * 10) / 10 + 'k' : Math.round(item?.value * 10) / 10
        );

    //VoLTE KPI:
    // VoLTE mobility
    const VoLTEMobilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'VO_LTE_MOBILITY', true);
    //IBC
    // VoLTE accessibility
    const VoLTEAccessibilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'VO_LTE_ACCESSIBILITY', true);
    //VoLTE retainability
    const VoLTERetainabilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'VO_LTE_RETAINABILITY', true);

    // New 5G Core KPIs
    const registeredDevicesKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ATTACHED_DEVICES', false);
    const registeredCount = registeredDevicesKPI?.value ? Number(registeredDevicesKPI?.value) : 0;
    const registeredDeviceCount = {
        enabledCount: registeredCount,
        offlineCount: totalDeviceCount - registeredCount,
        totalCount: totalDeviceCount
    };

    const registrationSuccessRateKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ATTACH_SUCCESS', true);

    // 5G Core throughput
    const core5GUpLinkKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'UPLINK_THROUGHPUT', false);
    const core5GDownLinkKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'DOWNLINK_THROUGHPUT', false);

    const core5GThroughputValues =
        core5GUpLinkKPI &&
        core5GDownLinkKPI &&
        [core5GDownLinkKPI, core5GUpLinkKPI].map((item) =>
            item?.value >= 1000 ? Math.round((item?.value / 1000) * 10) / 10 + 'k' : Math.round(item?.value * 10) / 10
        );

    // New 4G Core KPIs for NDAC
    const core4GAttachedDevicesKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ATTACHED_DEVICES', false);
    const core4GEnabledCount = core4GAttachedDevicesKPI?.value ? Number(core4GAttachedDevicesKPI?.value) : 0;
    const core4GDeviceCount = {
        enabledCount: core4GEnabledCount,
        offlineCount: totalDeviceCount - core4GEnabledCount,
        totalCount: totalDeviceCount
    };

    const core4GAttachSuccessRateKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ATTACH_SUCCESS', true);

    // Core network utilization
    const coreNetworkUtilizationKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'CORE_NETWORK_UTILIZATION', false);
    if (coreNetworkUtilizationKPI) {
        coreNetworkUtilizationKPI.value = coreNetworkUtilizationKPI.value
            ? Number(coreNetworkUtilizationKPI.value.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0])
            : 0;
    }

    // 4G Core throughput
    const core4GUpLinkKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'UPLINK_THROUGHPUT', false);
    const core4GDownLinkKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'DOWNLINK_THROUGHPUT', false);

    const core4GThroughputValues =
        core4GUpLinkKPI &&
        core4GDownLinkKPI &&
        [core4GDownLinkKPI, core4GUpLinkKPI].map((item) =>
            item?.value >= 1000 ? Math.round((item?.value / 1000) * 10) / 10 + 'k' : Math.round(item?.value * 10) / 10
        );

    // Widget configurations with section assignments
    const widgetConfigs = [
        // 5G Core Section Widgets (for NDAC 5G SA)
        {
            section: widgetSections.CORE_5G,
            condition: show5GCoreSection && isKPI5GSARegisteredDevices && registeredDevicesKPI,
            props: {
                title: 'Registered devices',
                thresholds: registeredDevicesKPI?.thresholds,
                kpiType: registeredDevicesKPI?.kpiType,
                defaultThresholds: registeredDevicesKPI?.defaultThresholds,
                deviceCount: registeredDeviceCount,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the total number of devices registered to the 5G SA core network',
                COLORS: ['#008331', '#4A1C02'],
                type: 'Registered devices',
                data: [
                    { name: 'Online', value: registeredDeviceCount?.enabledCount },
                    { name: 'Offline', value: registeredDeviceCount?.offlineCount }
                ],
                leftValue: '-7px'
            }
        },
        {
            section: widgetSections.CORE_5G,
            condition: show5GCoreSection && isKPI5GSARegistrationSuccessRate && registrationSuccessRateKPI,
            props: {
                title: 'Registration success rate',
                KPIValue: registrationSuccessRateKPI?.value,
                thresholds: registrationSuccessRateKPI?.thresholds,
                kpiType: registrationSuccessRateKPI?.kpiType,
                defaultThresholds: registrationSuccessRateKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the successful registration percentage out of total attempts for 5G SA',
                COLORS: ['#A40000', '#FFBC3D', '#008331'],
                type: 'Registration success rate',
                data: [
                    { name: 'level1', value: registrationSuccessRateKPI?.thresholds?.level1 },
                    { name: 'level2', value: registrationSuccessRateKPI?.thresholds?.level2 },
                    { name: 'level3', value: registrationSuccessRateKPI?.thresholds?.level3 }
                ],
                leftValue: '5px'
            }
        },
        {
            section: widgetSections.CORE_5G,
            condition: show5GCoreSection && (isKPI5GCoreDLThroughput || isKPI5GCoreULThroughput) && core5GThroughputValues,
            props: {
                title: 'Core throughput',
                throughputValues: core5GThroughputValues,
                onSubmit: submitModifyKpi,
                tooltip: 'Shows downlink and uplink throughput of the 5G core network',
                COLORS: [],
                type: '5G Core throughput',
                data: [],
                leftValue: '-10px'
            }
        },
        // 4G Core Section Widgets (for NDAC LTE/5G NSA)
        {
            section: widgetSections.CORE_4G,
            condition: show4GCoreSection && isKPI4GCoreAttachedDevices && core4GAttachedDevicesKPI,
            props: {
                title: 'Attached devices',
                thresholds: core4GAttachedDevicesKPI?.thresholds,
                kpiType: core4GAttachedDevicesKPI?.kpiType,
                defaultThresholds: core4GAttachedDevicesKPI?.defaultThresholds,
                deviceCount: core4GDeviceCount,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the total number of devices attached to the 4G core network',
                COLORS: ['#008331', '#4A1C02'],
                type: 'Attached devices',
                data: [
                    { name: 'Online', value: core4GDeviceCount?.enabledCount },
                    { name: 'Offline', value: core4GDeviceCount?.offlineCount }
                ],
                leftValue: '-7px'
            }
        },
        {
            section: widgetSections.CORE_4G,
            condition: show4GCoreSection && isKPI4GCoreAttachSuccessRate && core4GAttachSuccessRateKPI,
            props: {
                title: 'Attach success rate',
                KPIValue: core4GAttachSuccessRateKPI?.value,
                thresholds: core4GAttachSuccessRateKPI?.thresholds,
                kpiType: core4GAttachSuccessRateKPI?.kpiType,
                defaultThresholds: core4GAttachSuccessRateKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the successful attach percentage out of total attempts for 4G core',
                COLORS: ['#A40000', '#FFBC3D', '#008331'],
                type: 'Attach success rate',
                data: [
                    { name: 'level1', value: core4GAttachSuccessRateKPI?.thresholds?.level1 },
                    { name: 'level2', value: core4GAttachSuccessRateKPI?.thresholds?.level2 },
                    { name: 'level3', value: core4GAttachSuccessRateKPI?.thresholds?.level3 }
                ],
                leftValue: '5px'
            }
        },
        {
            section: widgetSections.CORE_EP5G,
            condition: isKPI4GCoreNetworkUtilization && coreNetworkUtilizationKPI,
            props: {
                title: 'Core network utilization',
                KPIValue: coreNetworkUtilizationKPI?.value,
                thresholds: coreNetworkUtilizationKPI?.thresholds,
                kpiType: coreNetworkUtilizationKPI?.kpiType,
                defaultThresholds: coreNetworkUtilizationKPI?.defaultThresholds,
                coreCapacity: coreCapacity,
                attachedDevicesCount: attachedDevicesCount,
                deviceCount: totalDeviceCount,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the percentage of users who are simultaneously attached to the network based on the session capacity limit',
                COLORS: ['#008331', '#FFBC3D', '#A40000'],
                type: 'Core network utilization',
                data: [
                    { name: 'level1', value: coreNetworkUtilizationKPI?.thresholds?.level1 },
                    { name: 'level2', value: coreNetworkUtilizationKPI?.thresholds?.level2 },
                    { name: 'level3', value: coreNetworkUtilizationKPI?.thresholds?.level3 }
                ],
                leftValue: '5px'
            }
        },
        {
            section: widgetSections.CORE_4G,
            condition: show4GCoreSection && (isKPI4GCoreDLThroughputNew || isKPI4GCoreULThroughputNew) && core4GThroughputValues,
            props: {
                title: 'Core throughput',
                throughputValues: core4GThroughputValues,
                onSubmit: submitModifyKpi,
                tooltip: 'Shows uplink and downlink throughput of the 4G core network',
                COLORS: [],
                type: 'Core throughput',
                data: [],
                leftValue: '-10px'
            }
        },
        // EP5G 4G/5G Core Section Widget
        {
            section: widgetSections.CORE_EP5G,
            condition: isEP5G && (isKPI4GAttachedDevices || isKPI5GSARegisteredDevices) && (attachedDevicesKPI || registeredDevicesKPI),
            props: {
                title: 'Attached devices',
                thresholds: (attachedDevicesKPI || registeredDevicesKPI)?.thresholds,
                kpiType: (attachedDevicesKPI || registeredDevicesKPI)?.kpiType,
                defaultThresholds: (attachedDevicesKPI || registeredDevicesKPI)?.defaultThresholds,
                deviceCount: attachedDevicesKPI ? deviceCount : registeredDeviceCount,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the total number of devices connected to the network',
                COLORS: ['#008331', '#4A1C02'],
                type: 'Attached devices',
                data: [
                    { name: 'Online', value: (attachedDevicesKPI ? deviceCount : registeredDeviceCount)?.enabledCount },
                    { name: 'Offline', value: (attachedDevicesKPI ? deviceCount : registeredDeviceCount)?.offlineCount }
                ],
                leftValue: '-7px'
            }
        },
        // Existing widgets (keeping the original ones for backward compatibility)
        {
            section: widgetSections.CORE_4G,
            condition:
                isKPI4GAttachedDevices &&
                attachedDevicesKPI &&
                !isIBC &&
                !isEP5G &&
                !show4GCoreSection &&
                !show5GCoreSection &&
                !NHN_PWN_disabled(solutionType, userRoles),
            props: {
                title: 'Attached devices',
                thresholds: attachedDevicesKPI?.thresholds,
                kpiType: attachedDevicesKPI?.kpiType,
                defaultThresholds: attachedDevicesKPI?.defaultThresholds,
                deviceCount: deviceCount,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the total number of devices connected to the network',
                COLORS: ['#008331', '#4A1C02'],
                type: 'Attached devices',
                data: [
                    { name: 'Online', value: deviceCount?.enabledCount },
                    { name: 'Offline', value: deviceCount?.offlineCount }
                ],
                leftValue: '-7px'
            }
        },
        {
            section: widgetSections.CORE_4G,
            condition:
                isKPI4GAttachSuccessRate &&
                attachRateKPI &&
                !isIBC &&
                !isEP5G &&
                !show4GCoreSection &&
                !show5GCoreSection &&
                !NHN_PWN_disabled(solutionType, userRoles),
            props: {
                title: 'Attach success rate',
                // title: 'Attached success',
                KPIValue: attachRateKPI?.value,
                thresholds: attachRateKPI?.thresholds,
                kpiType: attachRateKPI?.kpiType,
                defaultThresholds: attachRateKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the successful attach percentage out of total attempts',
                COLORS: ['#A40000', '#FFBC3D', '#008331'],
                type: 'Attach success rate',
                data: [
                    { name: 'level1', value: attachRateKPI?.thresholds?.level1 },
                    { name: 'level2', value: attachRateKPI?.thresholds?.level2 },
                    { name: 'level3', value: attachRateKPI?.thresholds?.level3 }
                ],
                leftValue: '5px'
            }
        },
        {
            section: widgetSections.CORE_4G,
            condition: isKPI4GAttachSuccessRate && attachedSuccessRatioKPI && isIBC && !isEP5G && !show4GCoreSection && !show5GCoreSection,
            props: {
                title: 'Attached success ratio',
                // title: 'Attached success',
                thresholds: attachedSuccessRatioKPI?.thresholds,
                kpiType: attachedSuccessRatioKPI?.kpiType,
                defaultThresholds: attachedSuccessRatioKPI?.defaultThresholds,
                deviceCount: successDeviceCount,
                onSubmit: submitModifyKpi,
                tooltip: 'Shows total number of devices success, connected and failures',
                COLORS: ['#008331', '#A40000'],
                type: 'Attached success ratio',
                data: [
                    { name: 'Success', value: deviceCount?.successCount },
                    { name: 'Failures', value: deviceCount?.failuresCount }
                ],
                leftValue: '20px'
            }
        },
        {
            section: widgetSections.CORE_4G,
            condition:
                (isKPI4GCoreDLThroughput || isKPI4GCoreULThroughput) &&
                throughputValues &&
                !isIBC &&
                !isEP5G &&
                !show4GCoreSection &&
                !show5GCoreSection &&
                !NHN_PWN_disabled(solutionType, userRoles),
            props: {
                // title: (isEP5G ? 'RAN' : 'Core') + ' combined throughput',
                title: 'Core throughput',
                throughputValues: throughputValues,
                isEP5G: isEP5GOrNhnPwn,
                onSubmit: submitModifyKpi,
                tooltip: `Shows uplink and downlink throughput of the core network`,
                COLORS: [],
                type: 'Core throughput',
                data: [],
                leftValue: '-10px'
            }
        },
        {
            section: widgetSections.RAN_DATA,
            condition: (isKPI4GRANDLThroughput || isKPI4GRANULThroughput) && throughputValues4g && !isIBC,
            props: {
                // title: 'RAN throughput',
                title: 'RAN throughput',
                throughputValues: throughputValues4g,
                isEP5G: isEP5GOrNhnPwn,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 4G downlink and uplink throughput of the RAN network',
                COLORS: [],
                type: '4G RAN throughput',
                data: [],
                leftValue: '-10px'
            }
        },
        {
            section: widgetSections.RAN_DATA,
            condition: (isKPI4GRANDLThroughput || isKPI4GRANULThroughput) && upLinkKPI && downLinkKPI && isIBC,
            props: {
                title: 'RAN throughput',
                throughputValues: throughputValues,
                isIBC: true,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 4G downlink and uplink throughput of the RAN network',
                COLORS: [],
                type: '4G RAN throughput',
                data: [],
                leftValue: '-10px'
            }
        },
        {
            section: widgetSections.RAN_DATA,
            condition: isKPI4GRANDataAccessibility && accessibilityKPI && (isEricssonSolution || isEP5GOrNhnPwn || is4gNDAC),
            props: {
                // title: is4gNDAC ? '4G RAN Accessibility' : 'Accessibility',
                title: 'Accessibility',
                KPIValue: accessibilityKPI?.value,
                thresholds: accessibilityKPI?.thresholds,
                kpiType: accessibilityKPI?.kpiType,
                defaultThresholds: accessibilityKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 4G blocked data session rate',
                COLORS: ['#008331', '#FFBC3D', '#A40000'],
                type: 'Accessibility',
                data: [
                    { name: 'level1', value: accessibilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: accessibilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: accessibilityKPI?.thresholds?.level3 }
                ],
                leftValue: is4gNDAC ? '13px' : '-30px'
            }
        },
        {
            section: widgetSections.RAN_DATA,
            condition: isKPI4GRANDataAccessibility && accessibilityKPI && isIBC,
            props: {
                // title: 'Data accessibility (blocked session rate)',
                title: 'Accessibility',
                KPIValue: accessibilityKPI?.value,
                thresholds: accessibilityKPI?.thresholds,
                kpiType: accessibilityKPI?.kpiType,
                defaultThresholds: accessibilityKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 4G blocked data session rate',
                COLORS: ['#008331', '#FFBC3D', '#A40000'],
                type: 'Accessibility',
                data: [
                    { name: 'level1', value: accessibilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: accessibilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: accessibilityKPI?.thresholds?.level3 }
                ],
                leftValue: '80px'
            }
        },
        {
            section: widgetSections.RAN_DATA,
            condition: isKPI4GRANDataRetainability && retainabilityKPI && (isEricssonSolution || isEP5GOrNhnPwn || is4gNDAC),
            props: {
                // title: is4gNDAC ? '4G RAN Retainability' : 'Retainability',
                title: 'Retainability',
                KPIValue: retainabilityKPI?.value,
                thresholds: retainabilityKPI?.thresholds,
                kpiType: retainabilityKPI?.kpiType,
                defaultThresholds: retainabilityKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 4G dropped data session rate',
                COLORS: ['#008331', '#FFBC3D', '#A40000'],
                type: 'Retainability',
                data: [
                    { name: 'level1', value: retainabilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: retainabilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: retainabilityKPI?.thresholds?.level3 }
                ],
                leftValue: is4gNDAC ? '11px' : '-32px'
            }
        },
        {
            section: widgetSections.RAN_DATA,
            condition: isKPI4GRANDataRetainability && retainabilityKPI && isIBC,
            props: {
                // title: 'Data retainability (dropped session rate)',
                title: 'Retainability',
                KPIValue: retainabilityKPI?.value,
                thresholds: retainabilityKPI?.thresholds,
                kpiType: retainabilityKPI?.kpiType,
                defaultThresholds: retainabilityKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 4G dropped data session rate',
                COLORS: ['#008331', '#FFBC3D', '#A40000'],
                type: 'Retainability',
                data: [
                    { name: 'level1', value: retainabilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: retainabilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: retainabilityKPI?.thresholds?.level3 }
                ],
                leftValue: '80px'
            }
        },
        {
            section: widgetSections.RAN_DATA,
            condition: isKPI4GRANDataMobility && mobilityKPI && (isEricssonSolution || isEP5GOrNhnPwn || is4gNDAC || isIBC || isNhnPwn),
            props: {
                // title: is4gNDAC ? '4G RAN Mobility' : 'Mobility',
                title: 'Mobility',
                KPIValue: mobilityKPI?.value,
                thresholds: mobilityKPI?.thresholds,
                kpiType: mobilityKPI?.kpiType,
                defaultThresholds: mobilityKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 4G data mobility success rate',
                COLORS: ['#A40000', '#FFBC3D', '#008331'],
                type: 'Mobility',
                data: [
                    { name: 'level1', value: mobilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: mobilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: mobilityKPI?.thresholds?.level3 }
                ],
                leftValue: is4gNDAC ? '-11px' : '-54px'
            }
        },
        {
            section: widgetSections.RAN_VOICE,
            condition: isKPIVoLTEAccessibility && VoLTEAccessibilityKPI,
            props: {
                // title: 'VoLTE accessibility',
                title: 'Accessibility',
                KPIValue: VoLTEAccessibilityKPI?.value,
                thresholds: VoLTEAccessibilityKPI?.thresholds,
                kpiType: VoLTEAccessibilityKPI?.kpiType,
                defaultThresholds: VoLTEAccessibilityKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 4G blocked voice session rate',
                COLORS: ['#008331', '#FFBC3D', '#A40000'],
                type: 'VoLTE accessibility',
                data: [
                    { name: 'level1', value: VoLTEAccessibilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: VoLTEAccessibilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: VoLTEAccessibilityKPI?.thresholds?.level3 }
                ],
                leftValue: '-30px'
            }
        },
        {
            section: widgetSections.RAN_VOICE,
            condition: isKPIVoLTERetainability && VoLTERetainabilityKPI,
            props: {
                // title: 'VoLTE retainability',
                title: 'Retainability',
                KPIValue: VoLTERetainabilityKPI?.value,
                thresholds: VoLTERetainabilityKPI?.thresholds,
                kpiType: VoLTERetainabilityKPI?.kpiType,
                defaultThresholds: VoLTERetainabilityKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 4G dropped voice session rate',
                COLORS: ['#008331', '#FFBC3D', '#A40000'],
                type: 'VoLTE retainability',
                data: [
                    { name: 'level1', value: VoLTERetainabilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: VoLTERetainabilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: VoLTERetainabilityKPI?.thresholds?.level3 }
                ],
                leftValue: '-30px'
            }
        },
        {
            section: widgetSections.RAN_VOICE,
            condition: isKPIVoLTEMobility && VoLTEMobilityKPI,
            props: {
                // title: 'VoLTE mobility',
                title: 'Mobility',
                KPIValue: VoLTEMobilityKPI?.value,
                thresholds: VoLTEMobilityKPI?.thresholds,
                kpiType: VoLTEMobilityKPI?.kpiType,
                defaultThresholds: VoLTEMobilityKPI?.defaultThresholds,
                isIBC: true,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 4G voice mobility success rate',
                COLORS: ['#A40000', '#FFBC3D', '#008331'],
                type: 'Voice Mobility',
                data: [
                    { name: 'level1', value: VoLTEMobilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: VoLTEMobilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: VoLTEMobilityKPI?.thresholds?.level3 }
                ],
                leftValue: '-55px'
            }
        },
        {
            section: widgetSections.NSA_RAN_DATA,
            condition: (isKPI5GRANDLThroughput || isKPI5GRANULThroughput) && throughputValues5g && isEP5G_NHN_PWN_Or_NDAC_5G,
            props: {
                // title: '5G NSA RAN throughput',
                title: 'RAN throughput',
                throughputValues: throughputValues5g,
                isEP5G: isEP5GOrNhnPwn,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 5G downlink and uplink throughput of the RAN network',
                COLORS: [],
                type: '5G NSA RAN throughput',
                data: [],
                leftValue: '-10px'
            }
        },
        {
            section: widgetSections.NSA_RAN_DATA,
            condition: isKPI5GNSAAccessibility && outdoor5gnbAccessibilityKPI && isEP5G_NHN_PWN_Or_NDAC_5G,
            props: {
                // title: '5G NSA accessibility',
                title: 'Accessibility',
                KPIValue: outdoor5gnbAccessibilityKPI?.value,
                thresholds: outdoor5gnbAccessibilityKPI?.thresholds,
                kpiType: outdoor5gnbAccessibilityKPI?.kpiType,
                defaultThresholds: outdoor5gnbAccessibilityKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 5G NSA blocked data session rate',
                COLORS: ['#008331', '#FFBC3D', '#A40000'],
                type: '5G NSA accessibility',
                data: [
                    { name: 'level1', value: outdoor5gnbAccessibilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: outdoor5gnbAccessibilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: outdoor5gnbAccessibilityKPI?.thresholds?.level3 }
                ],
                leftValue: '-30px'
            }
        },
        {
            section: widgetSections.NSA_RAN_DATA,
            condition: isKPI5GNSARetainability && outdoor5gnbRetainabilityKPI && isEP5G_NHN_PWN_Or_NDAC_5G,
            props: {
                // title: '5G NSA retainability',
                title: 'Retainability',
                KPIValue: outdoor5gnbRetainabilityKPI?.value,
                thresholds: outdoor5gnbRetainabilityKPI?.thresholds,
                kpiType: outdoor5gnbRetainabilityKPI?.kpiType,
                defaultThresholds: outdoor5gnbRetainabilityKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 5G NSA dropped data session rate',
                COLORS: ['#008331', '#FFBC3D', '#A40000'],
                type: '5G NSA retainability',
                data: [
                    { name: 'level1', value: outdoor5gnbRetainabilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: outdoor5gnbRetainabilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: outdoor5gnbRetainabilityKPI?.thresholds?.level3 }
                ],
                leftValue: '-30px'
            }
        },
        {
            section: widgetSections.NSA_RAN_DATA,
            condition: isKPI5GNSAMobility && outdoor5gnbMobilityKPI && isEP5G_NHN_PWN_Or_NDAC_5G && !NHN_PWN_disabled(solutionType, userRoles),
            props: {
                title: 'Mobility',
                KPIValue: outdoor5gnbMobilityKPI?.value,
                thresholds: outdoor5gnbMobilityKPI?.thresholds,
                kpiType: outdoor5gnbMobilityKPI?.kpiType,
                defaultThresholds: outdoor5gnbMobilityKPI?.defaultThresholds,
                onSubmit: submitModifyKpi,
                tooltip: 'This shows the 5G NSA data mobility success rate',
                COLORS: ['#A40000', '#FFBC3D', '#008331'],
                type: '5G NSA mobility',
                data: [
                    { name: 'level1', value: outdoor5gnbMobilityKPI?.thresholds?.level1 },
                    { name: 'level2', value: outdoor5gnbMobilityKPI?.thresholds?.level2 },
                    { name: 'level3', value: outdoor5gnbMobilityKPI?.thresholds?.level3 }
                ],
                leftValue: '-30px'
            }
        },
        {
            section: widgetSections.RAN_DATA_5G,
            condition: (isKPI5GRANDLThroughput || isKPI5GRANULThroughput) && gnbUpLinkKPI && gnbDownLinkKPI && isIBC,
            props: {
                title: 'RAN throughput',
                throughputValues: throughputValues5gRAN,
                isIBC: true,
                onSubmit: submitModifyKpi,
                tooltip: `This shows the 5G downlink and uplink throughput of the RAN network`,
                COLORS: [],
                type: '5G RAN throughput',
                data: [],
                leftValue: '-10px'
            }
        }
    ];

    const handleClick = (index) => {
        setActiveIndices((prevIndices) => (prevIndices.includes(index) ? prevIndices.filter((i) => i !== index) : [...prevIndices, index]));
    };

    const renderWidgetsBySection = (section) =>
        widgetConfigs
            .filter((config) => config.section === section && config.condition)
            .map((config, index) => (
                <Card raised={true} key={index}>
                    <OverallWidget loading={loading} enterpriseId={enterpriseId} locationId={locationId} {...config.props} />
                </Card>
            ));

    return (
        <>
            {loading ? (
                <LoadingPage />
            ) : (
                <Grid.Column computer="11" mobile="12">
                    {errorMessage && (
                        <Grid.Row>
                            <Grid.Column computer="11" mobile="12">
                                <ErrorMessage onDismiss={errorMessageDismiss} errors={errorMessage} />
                            </Grid.Column>
                        </Grid.Row>
                    )}

                    <Grid.Row computer="11" mobile="12" className="kpi-core-grid">
                        <Accordion fluid className="performance-accordion">
                            {Object.entries(widgetSections).map(([key, sectionName], index) => {
                                const widgets = renderWidgetsBySection(sectionName);
                                if (!widgets || widgets.length === 0) {
                                    return null; // Skip rendering if no widgets
                                }
                                return (
                                    <>
                                        <Accordion.Title
                                            active={activeIndices.includes(index)}
                                            icon={false}
                                            className="accordion-title"
                                            content={
                                                <div className="accordion-title-content">
                                                    <h4>{sectionName}</h4>
                                                    <Icon name="dropdown" />
                                                </div>
                                            }
                                            index={index}
                                            onClick={() => handleClick(index)}
                                        />
                                        <Accordion.Content active={activeIndices.includes(index)} className="accordion-content">
                                            <Card.Group itemsPerRow={3}>{widgets}</Card.Group>
                                        </Accordion.Content>
                                    </>
                                );
                            })}
                        </Accordion>
                    </Grid.Row>
                </Grid.Column>
            )}
        </>
    );
};

export default KPIViewPage;
